/*
 * SupplyKart shared storefront interactions
 * Version: 3.0.0
 *
 * Install as: assets/supplykart-components.js
 * Load before </body> or in <head> with defer:
 * <script src="{{ 'supplykart-components.js' | asset_url }}" defer></script>
 *
 * Components are progressively enhanced through data-sk-* attributes. The
 * file has no framework dependency and is safe to load once on every page.
 */

(() => {
  'use strict';

  if (window.SupplyKartComponents?.loaded) return;

  const root = document.documentElement;
  const SELECTOR_FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const state = {
    activeLayer: null,
    lastTrigger: null,
    lastIntent: null,
    layerTriggers: new WeakMap(),
    layerOpenState: new WeakMap(),
    prefetched: new Set(),
    rfq: []
  };

  const LEGACY_LAYER_SELECTOR = [
    '.sk-modal', '.ska-modal', '.skb-modal', '.skb-rfq-drawer', '.skc-modal',
    '.skd-modal', '.skd-rfq', '.skd-filter-panel', '.sk-drawer', '.ski-modal',
    '.ski-rfq-drawer', '.skk-modal', '.skk-rfq-drawer', '.skp-modal',
    '.skp-rfq-drawer', '.skpp-modal', '.sks-modal', '.sks-rfq-drawer',
    '.sksol-modal', '.skf-modal', '.skm-modal'
  ].map(selector => `[data-sk-section-root] ${selector}`).join(',');

  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const escapeSelector = value => window.CSS?.escape ? CSS.escape(value) : String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  const shopRoot = () => window.Shopify?.routes?.root || '/';

  function emit(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  }

  function parseJSON(value, fallback = null) {
    try {
      return JSON.parse(value);
    } catch (_) {
      return fallback;
    }
  }

  function debounce(callback, delay = 180) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => callback(...args), delay);
    };
  }

  function announce(message) {
    let region = qs('[data-sk-live-region]');
    if (!region) {
      region = document.createElement('div');
      region.className = 'sk-ui-visually-hidden';
      region.dataset.skLiveRegion = '';
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      document.body.appendChild(region);
    }
    region.textContent = '';
    window.requestAnimationFrame(() => { region.textContent = message; });
  }

  function toast(message, type = 'default', duration = 3600) {
    let region = qs('[data-sk-toast-region]');
    if (!region) {
      region = document.createElement('div');
      region.className = 'sk-ui-toast-region';
      region.dataset.skToastRegion = '';
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }

    const item = document.createElement('div');
    item.className = `sk-ui-toast${type !== 'default' ? ` sk-ui-toast--${type}` : ''}`;
    item.setAttribute('role', type === 'error' ? 'alert' : 'status');
    item.textContent = message;
    region.appendChild(item);
    announce(message);

    window.setTimeout(() => {
      item.style.opacity = '0';
      item.style.transform = 'translateY(6px)';
      window.setTimeout(() => item.remove(), 200);
    }, duration);
  }

  function setLoading(button, loading) {
    if (!button) return;
    if (loading) {
      button.dataset.skOriginalText = button.textContent.trim();
      button.classList.add('sk-ui-is-loading');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    } else {
      if (button.dataset.skOriginalText) button.textContent = button.dataset.skOriginalText;
      button.classList.remove('sk-ui-is-loading');
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  function resolveTarget(trigger, attribute) {
    const value = trigger.getAttribute(attribute);
    if (!value) return null;
    const selector = value.startsWith('#') || value.startsWith('.') || value.startsWith('[')
      ? value
      : `#${escapeSelector(value)}`;
    return qs(selector);
  }

  function ensureOverlay() {
    let overlay = qs('[data-sk-overlay]');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'sk-ui-overlay';
      overlay.dataset.skOverlay = '';
      document.body.appendChild(overlay);
    }
    return overlay;
  }

  function openLayer(layer, trigger = null) {
    if (!layer) return;
    if (state.activeLayer && state.activeLayer !== layer) closeLayer(state.activeLayer, false);

    state.activeLayer = layer;
    state.lastTrigger = trigger || document.activeElement;
    const overlay = ensureOverlay();

    layer.classList.add('is-open');
    layer.removeAttribute('hidden');
    layer.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-open');
    root.classList.add('sk-ui-scroll-lock');

    const focusTarget = qs('[autofocus]', layer) || qs(SELECTOR_FOCUSABLE, layer);
    window.setTimeout(() => focusTarget?.focus({ preventScroll: true }), 40);
    emit('supplykart:layer:open', { layer, trigger });
  }

  function closeLayer(layer = state.activeLayer, restoreFocus = true) {
    if (!layer) return;
    layer.classList.remove('is-open');
    layer.setAttribute('aria-hidden', 'true');
    ensureOverlay().classList.remove('is-open');
    root.classList.remove('sk-ui-scroll-lock');

    if (state.activeLayer === layer) state.activeLayer = null;
    if (restoreFocus && state.lastTrigger?.isConnected) state.lastTrigger.focus({ preventScroll: true });
    emit('supplykart:layer:close', { layer });
  }

  function legacyLayerIsOpen(layer) {
    return layer.classList.contains('is-open') && layer.getAttribute('aria-hidden') !== 'true';
  }

  function getLegacyOpenLayers() {
    return qsa(LEGACY_LAYER_SELECTOR).filter(legacyLayerIsOpen);
  }

  function syncLegacyLayers() {
    const layers = qsa(LEGACY_LAYER_SELECTOR);
    let hasOpenLayer = false;

    layers.forEach(layer => {
      const open = legacyLayerIsOpen(layer);
      const wasOpen = state.layerOpenState.get(layer) === true;
      state.layerOpenState.set(layer, open);
      hasOpenLayer ||= open;

      if (open && !wasOpen) {
        const trigger = state.lastIntent;
        if (trigger?.isConnected && !layer.contains(trigger)) state.layerTriggers.set(layer, trigger);
      }

      if (!open && wasOpen) {
        const trigger = state.layerTriggers.get(layer);
        state.layerTriggers.delete(layer);
        window.requestAnimationFrame(() => {
          if (!getLegacyOpenLayers().length && trigger?.isConnected) {
            trigger.focus?.({ preventScroll: true });
          }
        });
      }
    });

    root.classList.toggle('sk-ui-scroll-lock', hasOpenLayer || Boolean(state.activeLayer));
    document.body?.classList.toggle('sk-ui-scroll-lock', hasOpenLayer || Boolean(state.activeLayer));
  }

  function activeLegacyDialog() {
    const layers = getLegacyOpenLayers();
    const layer = layers[layers.length - 1];
    if (!layer) return null;
    return layer.matches('[aria-modal="true"]') ? layer : qs('[aria-modal="true"]', layer) || layer;
  }

  function trapLegacyFocus(event) {
    if (event.key !== 'Tab' || state.activeLayer) return;
    const dialog = activeLegacyDialog();
    if (!dialog) return;
    const focusable = qsa(SELECTOR_FOCUSABLE, dialog).filter(element => {
      return element.offsetParent !== null && element.getAttribute('aria-hidden') !== 'true';
    });
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  }

  function trapFocus(event) {
    const layer = state.activeLayer;
    if (!layer || event.key !== 'Tab') return;
    const focusable = qsa(SELECTOR_FOCUSABLE, layer).filter(element => element.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function initTabs(context = document) {
    qsa('[data-sk-tabs]', context).forEach((tabs, groupIndex) => {
      if (tabs.dataset.skReady) return;
      tabs.dataset.skReady = 'true';

      const triggers = qsa('[data-sk-tab]', tabs);
      const panels = qsa('[data-sk-tab-panel]', tabs);
      triggers.forEach((trigger, index) => {
        const targetId = trigger.dataset.skTab || panels[index]?.id || `sk-panel-${groupIndex}-${index}`;
        const panel = qs(`#${escapeSelector(targetId)}`, tabs) || panels[index];
        if (!panel) return;
        if (!panel.id) panel.id = targetId;
        if (!trigger.id) trigger.id = `${panel.id}-tab`;
        trigger.setAttribute('role', 'tab');
        trigger.setAttribute('aria-controls', panel.id);
        trigger.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        trigger.tabIndex = index === 0 ? 0 : -1;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', trigger.id);
        panel.hidden = index !== 0;
      });
    });
  }

  function activateTab(trigger) {
    const tabs = trigger.closest('[data-sk-tabs]');
    if (!tabs) return;
    const triggers = qsa('[data-sk-tab]', tabs);
    const panels = qsa('[data-sk-tab-panel]', tabs);
    const targetId = trigger.getAttribute('aria-controls') || trigger.dataset.skTab;

    triggers.forEach(item => {
      const selected = item === trigger;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
    });
    panels.forEach(panel => { panel.hidden = panel.id !== targetId; });
    emit('supplykart:tab:change', { tabs, trigger, targetId });
  }

  function initAccordions(context = document) {
    qsa('[data-sk-accordion-trigger]', context).forEach((trigger, index) => {
      if (trigger.dataset.skReady) return;
      trigger.dataset.skReady = 'true';
      const panel = resolveTarget(trigger, 'data-sk-accordion-trigger') || trigger.nextElementSibling;
      if (!panel) return;
      if (!panel.id) panel.id = `sk-accordion-panel-${index}-${Date.now()}`;
      trigger.setAttribute('aria-controls', panel.id);
      trigger.setAttribute('aria-expanded', trigger.getAttribute('aria-expanded') || 'false');
      panel.hidden = trigger.getAttribute('aria-expanded') !== 'true';
    });
  }

  function toggleAccordion(trigger) {
    const panelId = trigger.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : trigger.nextElementSibling;
    if (!panel) return;
    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!expanded));
    panel.hidden = expanded;
  }

  function updateQuantity(button) {
    const wrapper = button.closest('[data-sk-quantity]');
    const input = qs('input[type="number"], input[name="quantity"]', wrapper);
    if (!input) return;

    const step = Number(input.step) || 1;
    const min = Number(input.min) || 1;
    const max = Number(input.max) || Number.POSITIVE_INFINITY;
    const direction = button.matches('[data-sk-quantity-plus]') ? 1 : -1;
    const value = Math.min(max, Math.max(min, (Number(input.value) || min) + direction * step));
    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    announce(`Quantity ${value}`);
  }

  async function fetchCart() {
    const response = await fetch(`${shopRoot()}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Unable to refresh the cart.');
    return response.json();
  }

  function updateCartCount(count) {
    qsa('[data-sk-cart-count], [data-cart-count]').forEach(element => {
      element.textContent = count;
      element.hidden = count < 1;
    });

    qsa('.cart-count-bubble').forEach(bubble => {
      const visibleCount = qs('span[aria-hidden="true"]', bubble) || qs('span', bubble);
      if (visibleCount) visibleCount.textContent = count;
      bubble.style.display = count > 0 ? '' : 'none';
    });
  }

  function replaceReturnedSections(sections) {
    if (!sections || typeof sections !== 'object') return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const current = document.getElementById(`shopify-section-${id}`);
      if (!current) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.getElementById(`shopify-section-${id}`) || parsed.body.firstElementChild;
      if (replacement) current.replaceWith(replacement);
    });
  }

  function openThemeCartDrawer() {
    const drawer = qs('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }

    const trigger = qs('[data-cart-drawer-open], [aria-controls="CartDrawer"], a[href="/cart"]');
    if (trigger && trigger.getAttribute('aria-controls') === 'CartDrawer') {
      trigger.click();
      return true;
    }
    return false;
  }

  async function addToCart(form, submitter = null) {
    const button = submitter || qs('[type="submit"]', form);
    const behavior = form.dataset.skCartBehavior || 'stay';
    const sections = form.dataset.skCartSections;
    const formData = new FormData(form);

    if (!formData.get('id')) {
      toast('Please select an available product option.', 'error');
      return;
    }

    if (sections && !formData.get('sections')) formData.append('sections', sections);
    setLoading(button, true);

    try {
      const response = await fetch(`${shopRoot()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
        credentials: 'same-origin'
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'This item could not be added.');

      replaceReturnedSections(result.sections);
      const cart = await fetchCart();
      updateCartCount(cart.item_count);
      emit('supplykart:cart:updated', { cart, item: result, source: form });
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));

      if (behavior === 'cart') {
        window.location.assign(`${shopRoot()}cart`);
        return;
      }

      if (behavior === 'drawer' && !openThemeCartDrawer()) {
        toast('Added to cart.', 'success');
      } else if (behavior !== 'drawer') {
        toast('Added to cart.', 'success');
      }
    } catch (error) {
      toast(error.message || 'Could not add this item. Please try again.', 'error', 5000);
      emit('supplykart:cart:error', { error, source: form });
    } finally {
      setLoading(button, false);
    }
  }

  function proxyCartSubmit(trigger) {
    const target = trigger.dataset.skCartSubmit;
    const form = target ? qs(target) : trigger.closest('[data-sk-product], .sk-root')?.querySelector('[data-sk-ajax-cart]');
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else qs('[type="submit"]', form)?.click();
  }

  const RFQ_STORAGE_KEY = 'supplykart-rfq-v1';

  function loadRFQ() {
    try {
      const value = parseJSON(localStorage.getItem(RFQ_STORAGE_KEY), []);
      state.rfq = Array.isArray(value) ? value : [];
    } catch (_) {
      state.rfq = [];
    }
    renderRFQ();
  }

  function saveRFQ() {
    try {
      localStorage.setItem(RFQ_STORAGE_KEY, JSON.stringify(state.rfq));
    } catch (_) {
      // Private browsing or storage restrictions should not block RFQ actions.
    }
    renderRFQ();
    emit('supplykart:rfq:updated', { items: [...state.rfq] });
  }

  function productFromTrigger(trigger) {
    return {
      id: String(trigger.dataset.productId || trigger.dataset.skRfqAdd || ''),
      variantId: String(trigger.dataset.variantId || ''),
      title: trigger.dataset.productTitle || 'Product',
      vendor: trigger.dataset.productVendor || '',
      image: trigger.dataset.productImage || '',
      url: trigger.dataset.productUrl || '',
      quantity: Number(trigger.dataset.quantity) || 1
    };
  }

  function addRFQ(trigger) {
    const item = productFromTrigger(trigger);
    if (!item.id) item.id = item.url || item.title;
    const key = `${item.id}:${item.variantId}`;
    const existing = state.rfq.find(candidate => `${candidate.id}:${candidate.variantId}` === key);
    if (existing) existing.quantity += item.quantity;
    else state.rfq.push(item);
    saveRFQ();
    trigger.setAttribute('aria-pressed', 'true');
    toast('Added to RFQ shortlist.', 'success');
  }

  function removeRFQ(trigger) {
    const key = trigger.dataset.skRfqRemove;
    state.rfq = state.rfq.filter(item => `${item.id}:${item.variantId}` !== key && item.id !== key);
    saveRFQ();
  }

  function renderRFQ() {
    qsa('[data-sk-rfq-count]').forEach(element => {
      element.textContent = state.rfq.length;
      element.hidden = state.rfq.length === 0;
    });

    const serialized = state.rfq.map(item => `${item.title}${item.variantId ? ` [${item.variantId}]` : ''} × ${item.quantity}`).join('\n');
    qsa('[data-sk-rfq-input]').forEach(input => { input.value = serialized; });

    qsa('[data-sk-rfq-list]').forEach(list => {
      list.textContent = '';
      if (!state.rfq.length) {
        const empty = document.createElement('p');
        empty.className = 'sk-muted';
        empty.textContent = 'Your RFQ shortlist is empty.';
        list.appendChild(empty);
        return;
      }

      state.rfq.forEach(item => {
        const row = document.createElement('div');
        row.className = 'sk-ui-file-preview';
        const label = document.createElement('span');
        label.textContent = `${item.title} × ${item.quantity}`;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'sk-ui-btn sk-ui-btn--ghost sk-ui-btn--small';
        remove.dataset.skRfqRemove = `${item.id}:${item.variantId}`;
        remove.textContent = 'Remove';
        row.append(label, remove);
        list.appendChild(row);
      });
    });
  }

  function clearRFQ() {
    state.rfq = [];
    saveRFQ();
    toast('RFQ shortlist cleared.');
  }

  function updateCharacterCount(input) {
    const selector = input.dataset.skCharacterCount;
    const counter = selector ? qs(selector) : input.parentElement?.querySelector('[data-sk-character-output]');
    if (!counter) return;
    const max = Number(input.maxLength) > 0 ? input.maxLength : Number(input.dataset.maxlength) || 0;
    counter.textContent = max ? `${input.value.length}/${max}` : String(input.value.length);
  }

  function initCharacterCounts(context = document) {
    qsa('[data-sk-character-count]', context).forEach(updateCharacterCount);
  }

  function validateFile(input) {
    const file = input.files?.[0];
    const dropzone = input.closest('[data-sk-dropzone]');
    const message = dropzone?.parentElement?.querySelector('[data-sk-file-message]');
    const preview = dropzone?.parentElement?.querySelector('[data-sk-file-preview]');
    if (!file) {
      if (preview) preview.hidden = true;
      return true;
    }

    const maxMb = Number(input.dataset.maxMb || dropzone?.dataset.maxMb || 10);
    const allowed = String(input.dataset.extensions || dropzone?.dataset.extensions || '')
      .toLowerCase().split(',').map(value => value.trim().replace(/^\./, '')).filter(Boolean);
    const extension = file.name.split('.').pop().toLowerCase();
    let error = '';
    if (allowed.length && !allowed.includes(extension)) error = `Use one of these file types: ${allowed.join(', ')}.`;
    else if (file.size > maxMb * 1024 * 1024) error = `File must be smaller than ${maxMb} MB.`;

    if (message) {
      message.textContent = error;
      message.hidden = !error;
      message.classList.toggle('sk-notice--error', Boolean(error));
    }
    if (error) {
      input.value = '';
      if (preview) preview.hidden = true;
      toast(error, 'error');
      return false;
    }

    if (preview) {
      preview.hidden = false;
      const name = qs('[data-sk-file-name]', preview);
      const size = qs('[data-sk-file-size]', preview);
      if (name) name.textContent = file.name;
      if (size) size.textContent = `${(file.size / 1024 / 1024).toFixed(2)} MB`;
    }
    return true;
  }

  function initDropzones(context = document) {
    qsa('[data-sk-dropzone]', context).forEach(dropzone => {
      if (dropzone.dataset.skReady) return;
      dropzone.dataset.skReady = 'true';
      const input = qs('input[type="file"]', dropzone);
      if (!input) return;

      ['dragenter', 'dragover'].forEach(name => dropzone.addEventListener(name, event => {
        event.preventDefault();
        dropzone.classList.add('is-dragging');
      }));
      ['dragleave', 'drop'].forEach(name => dropzone.addEventListener(name, event => {
        event.preventDefault();
        dropzone.classList.remove('is-dragging');
      }));
      dropzone.addEventListener('drop', event => {
        if (!event.dataTransfer?.files?.length) return;
        try {
          const transfer = new DataTransfer();
          transfer.items.add(event.dataTransfer.files[0]);
          input.files = transfer.files;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (_) {
          toast('Use the file picker to select this document.', 'error');
        }
      });
    });
  }

  function initLiveSearch(context = document) {
    qsa('[data-sk-live-search]', context).forEach(input => {
      if (input.dataset.skReady) return;
      input.dataset.skReady = 'true';
      input.addEventListener('input', debounce(() => {
        const container = input.dataset.skLiveSearch ? qs(input.dataset.skLiveSearch) : input.closest('.sk-root');
        if (!container) return;
        const query = input.value.trim().toLowerCase();
        const items = qsa('[data-sk-search-item]', container);
        let visible = 0;
        items.forEach(item => {
          const content = (item.dataset.skSearchItem || item.textContent).toLowerCase();
          const match = !query || content.includes(query);
          item.hidden = !match;
          if (match) visible += 1;
        });
        const count = qs('[data-sk-search-count]', container);
        if (count) count.textContent = visible;
        const empty = qs('[data-sk-search-empty]', container);
        if (empty) empty.hidden = visible !== 0;
      }, 120));
    });
  }

  function initReveal(context = document) {
    const items = qsa('[data-sk-reveal]:not(.is-visible)', context);
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach(item => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -7% 0px', threshold: 0.08 });
    items.forEach(item => observer.observe(item));
  }

  function canPrefetch(link) {
    if (!link || link.origin !== location.origin) return false;
    if (link.target === '_blank' || link.hasAttribute('download')) return false;
    if (link.dataset.noPrefetch !== undefined || link.relList?.contains('nofollow')) return false;
    if (link.searchParams.has('q') || link.pathname.startsWith('/search')) return false;
    return link.pathname === '/' || /^\/(pages|products|collections|blogs)(\/|$)/.test(link.pathname);
  }

  function prefetchLink(link) {
    if (!canPrefetch(link) || state.prefetched.has(link.href)) return;
    state.prefetched.add(link.href);
    const hint = document.createElement('link');
    hint.rel = 'prefetch';
    hint.as = 'document';
    hint.href = link.href;
    document.head.appendChild(hint);
  }

  const prefetchOnIntent = debounce(link => prefetchLink(link), 70);

  function onClick(event) {
    const target = event.target;
    const placeholderLink = target.closest('a[href="#"], a[href=""]');
    const modalOpen = target.closest('[data-sk-modal-open]');
    const drawerOpen = target.closest('[data-sk-drawer-open]');
    const close = target.closest('[data-sk-close]');
    const tab = target.closest('[data-sk-tab]');
    const accordion = target.closest('[data-sk-accordion-trigger]');
    const quantity = target.closest('[data-sk-quantity-plus], [data-sk-quantity-minus]');
    const cartProxy = target.closest('[data-sk-cart-submit]');
    const rfqAdd = target.closest('[data-sk-rfq-product], [data-skp]') ? null : target.closest('[data-sk-rfq-add]');
    const rfqRemove = target.closest('[data-sk-rfq-remove]');
    const rfqClear = target.closest('[data-sk-rfq-clear]');
    const fileRemove = target.closest('[data-sk-file-remove]');

    if (placeholderLink?.closest('[data-sk-section-root]')) {
      event.preventDefault();
      placeholderLink.dataset.skPlaceholderLink = '';
      placeholderLink.setAttribute('aria-disabled', 'true');
    } else if (modalOpen) {
      event.preventDefault();
      openLayer(resolveTarget(modalOpen, 'data-sk-modal-open'), modalOpen);
    } else if (drawerOpen) {
      event.preventDefault();
      openLayer(resolveTarget(drawerOpen, 'data-sk-drawer-open'), drawerOpen);
    } else if (close || target.matches('[data-sk-overlay]')) {
      event.preventDefault();
      closeLayer();
    } else if (tab) {
      event.preventDefault();
      activateTab(tab);
    } else if (accordion) {
      event.preventDefault();
      toggleAccordion(accordion);
    } else if (quantity) {
      event.preventDefault();
      updateQuantity(quantity);
    } else if (cartProxy) {
      event.preventDefault();
      proxyCartSubmit(cartProxy);
    } else if (rfqAdd) {
      event.preventDefault();
      addRFQ(rfqAdd);
    } else if (rfqRemove) {
      event.preventDefault();
      removeRFQ(rfqRemove);
    } else if (rfqClear) {
      event.preventDefault();
      clearRFQ();
    } else if (fileRemove) {
      event.preventDefault();
      const wrapper = fileRemove.closest('[data-sk-file-wrapper]') || fileRemove.parentElement?.parentElement;
      const input = qs('input[type="file"]', wrapper);
      if (input) input.value = '';
      const preview = qs('[data-sk-file-preview]', wrapper);
      if (preview) preview.hidden = true;
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape' && state.activeLayer) {
      event.preventDefault();
      closeLayer();
      return;
    }
    trapFocus(event);
    trapLegacyFocus(event);

    const tab = event.target.closest?.('[data-sk-tab]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const triggers = qsa('[data-sk-tab]', tab.closest('[data-sk-tabs]'));
    let index = triggers.indexOf(tab);
    if (event.key === 'ArrowRight') index = (index + 1) % triggers.length;
    if (event.key === 'ArrowLeft') index = (index - 1 + triggers.length) % triggers.length;
    if (event.key === 'Home') index = 0;
    if (event.key === 'End') index = triggers.length - 1;
    event.preventDefault();
    triggers[index].focus();
    activateTab(triggers[index]);
  }

  function onSubmit(event) {
    const form = event.target;
    if (!form.matches('[data-sk-ajax-cart]')) return;
    event.preventDefault();
    addToCart(form, event.submitter);
  }

  function onInput(event) {
    if (event.target.matches('[data-sk-character-count]')) updateCharacterCount(event.target);
  }

  function onChange(event) {
    if (event.target.matches('input[type="file"][data-sk-file], [data-sk-dropzone] input[type="file"]')) {
      validateFile(event.target);
    }
  }

  function init(context = document) {
    root.classList.add('sk-js');
    initTabs(context);
    initAccordions(context);
    initCharacterCounts(context);
    initDropzones(context);
    initLiveSearch(context);
    initReveal(context);
    renderRFQ();
    qsa('a[href="#"], a[href=""]', context).forEach(link => {
      if (!link.closest('[data-sk-section-root]')) return;
      link.dataset.skPlaceholderLink = '';
      link.setAttribute('aria-disabled', 'true');
    });
    syncLegacyLayers();
    emit('supplykart:ready', { context });
  }

  document.addEventListener('click', onClick);
  document.addEventListener('pointerdown', event => {
    const trigger = event.target.closest?.('button, a[href], summary, [role="button"]');
    if (trigger?.closest('[data-sk-section-root]')) state.lastIntent = trigger;
  }, { capture: true, passive: true });
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('submit', onSubmit);
  document.addEventListener('input', onInput);
  document.addEventListener('change', onChange);
  document.addEventListener('pointerover', event => {
    const link = event.target.closest?.('a[href]');
    if (link) prefetchOnIntent(link);
  }, { passive: true });
  document.addEventListener('touchstart', event => {
    const link = event.target.closest?.('a[href]');
    if (link) prefetchLink(link);
  }, { passive: true });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', syncLegacyLayers);

  let layerSyncQueued = false;
  const layerObserver = new MutationObserver(records => {
    const relevant = records.some(record => {
      return (record.attributeName === 'class' || record.attributeName === 'aria-hidden') &&
        record.target.closest?.('[data-sk-section-root]');
    });
    if (!relevant || layerSyncQueued) return;
    layerSyncQueued = true;
    window.requestAnimationFrame(() => {
      layerSyncQueued = false;
      syncLegacyLayers();
    });
  });
  layerObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'aria-hidden'],
    subtree: true
  });

  loadRFQ();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  window.SupplyKartComponents = {
    loaded: true,
    version: '3.0.0',
    init,
    open: openLayer,
    close: closeLayer,
    toast,
    addToCart,
    getRFQ: () => [...state.rfq],
    clearRFQ
  };
})();
